import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Map of sidebar route -> count of pending/unread items.
 * Counts run in parallel; safe-fail to 0 on any error.
 */
export type SidebarCounts = Record<string, number>;

// Cast to any to avoid deep generic inference on count-only queries.
const sb = supabase as unknown as {
  from: (table: string) => any;
  channel: (name: string) => any;
  removeChannel: (c: any) => void;
};

async function safeCount(builder: any): Promise<number> {
  try {
    const { count, error } = await builder;
    if (error) return 0;
    return count || 0;
  } catch {
    return 0;
  }
}

export function useSidebarCounts(): SidebarCounts {
  const { user } = useAuth();

  // Defer first fetch so it doesn't compete with the initial render burst.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!user?.id) return;
    const idle = (cb: () => void) => {
      const w = window as any;
      if (typeof w.requestIdleCallback === "function") {
        return w.requestIdleCallback(cb, { timeout: 1500 });
      }
      return window.setTimeout(cb, 800);
    };
    const cancel = (id: any) => {
      const w = window as any;
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(id);
      else window.clearTimeout(id);
    };
    const id = idle(() => setReady(true));
    return () => cancel(id);
  }, [user?.id]);

  const query = useQuery({
    queryKey: ["sidebar-counts", user?.id],
    enabled: !!user?.id && ready,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<SidebarCounts> => {
      const [
        pendingProfiles,
        pendingRegistrations,
        pendingContracts,
        pendingBudgets,
        unreadNotifications,
        pendingGuardianInvites,
        newContactSubmissions,
        pendingPickups,
        pendingAbsences,
        unreadStaffMessages,
      ] = await Promise.all([
        safeCount(
          sb.from("profiles").select("id", { count: "exact", head: true }).eq("status", "pending")
        ),
        safeCount(
          sb
            .from("child_registrations")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending")
        ),
        safeCount(
          sb
            .from("enrollment_contracts")
            .select("id", { count: "exact", head: true })
            .in("status", ["pending", "sent", "signing"])
        ),
        safeCount(
          sb.from("budgets").select("id", { count: "exact", head: true }).eq("status", "pending")
        ),
        safeCount(
          sb
            .from("notifications")
            .select("id", { count: "exact", head: true })
            .eq("user_id", user!.id)
            .eq("is_read", false)
        ),
        safeCount(
          sb
            .from("guardian_invitations")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending")
        ),
        safeCount(
          sb
            .from("contact_submissions")
            .select("id", { count: "exact", head: true })
            .eq("is_read", false)
        ),
        safeCount(
          sb
            .from("pickup_notifications")
            .select("id", { count: "exact", head: true })
            .is("read_at", null)
            .eq("is_active", true)
        ),
        safeCount(
          sb
            .from("employee_absences")
            .select("id", { count: "exact", head: true })
            .eq("status", "pending")
        ),
        safeCount(
          sb
            .from("staff_messages")
            .select("id", { count: "exact", head: true })
            .eq("is_read", false)
            .neq("sender_id", user!.id)
        ),
      ]);

      return {
        "/painel/aprovacoes": pendingProfiles + pendingRegistrations,
        "/painel/convites-pais": pendingGuardianInvites,
        "/painel/contratos": pendingContracts,
        "/painel/orcamentos": pendingBudgets,
        "/painel/notificacoes": unreadNotifications,
        "/painel/contatos": newContactSubmissions,
        "/painel/historico-retiradas": pendingPickups,
        "/painel/ausencias": pendingAbsences,
        "/painel/chat": unreadStaffMessages,
      };
    },
  });

  // Realtime invalidation on the most active tables
  useEffect(() => {
    if (!user?.id) return;
    const channel = sb
      .channel("sidebar-counts-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => query.refetch()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "child_registrations" },
        () => query.refetch()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles" },
        () => query.refetch()
      )
      .subscribe();
    return () => {
      sb.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return query.data || {};
}
